import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  SP,
  useMembers,
  useMember as useSPMember,
} from "../http/serviceportalApi";
import { RemoteDataStatus } from "../types";
import { getRemoteDataStatus, mergeStatuses } from "../utils";

type MemberContextType = {
  member?: SP.Member;
  hasOnlyOneMember?: boolean;
  status: RemoteDataStatus;
  error?: unknown;
  setMember: (member: SP.Member) => void;
};

const MemberContext = createContext<MemberContextType>(undefined!);

const LOCAL_STORAGE_KEY_MEMBER = "selected-member";

export const MemberProvider = ({ children }: PropsWithChildren) => {
  const [memberId, setMemberId] = useState<number | undefined>(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY_MEMBER);
    return saved ? parseInt(saved) : undefined;
  });

  const setAndPersistMemberId = useCallback((id: number | undefined) => {
    setMemberId(id);
    if (id) {
      localStorage.setItem(LOCAL_STORAGE_KEY_MEMBER, id.toString());
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEY_MEMBER);
    }
  }, []);

  const navigate = useNavigate();

  const setMember = useCallback(
    (member?: SP.Member) => {
      aborter.current.abort();
      aborter.current = new AbortController();
      setAndPersistMemberId(member?.id);
      navigate("/");
    },
    [setAndPersistMemberId, navigate],
  );

  const {
    data,
    isValidating: isMemberListValidating,
    error: memberListError,
  } = useMembers({ page: 1, page_size: 1 });
  const firstMember = data?.items[0];
  const memberListStatus = getRemoteDataStatus({
    isValidating: isMemberListValidating,
    error: memberListError,
  });

  useEffect(() => {
    if (!memberId && firstMember) {
      setAndPersistMemberId(firstMember.id);
    }
  }, [firstMember, memberId, setAndPersistMemberId]);

  const aborter = useRef(new AbortController());
  const {
    data: member,
    isValidating: isMemberValidating,
    error: memberError,
  } = useSPMember(memberId, aborter.current.signal);
  const memberStatus = getRemoteDataStatus({
    isValidating: isMemberValidating,
    error: memberError,
  });

  useEffect(() => {
    if (memberError?.status === 403) {
      setAndPersistMemberId(undefined);
    }
  }, [memberError]);

  const value = {
    member,
    hasOnlyOneMember: data?.next_page === undefined,
    status: mergeStatuses([memberListStatus, memberStatus]),
    error: memberListError || memberError,
    setMember,
  };

  return (
    <MemberContext.Provider value={value}>{children}</MemberContext.Provider>
  );
};

export const useMember = () => {
  return useContext(MemberContext);
};
